import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import BusinessCard from "@/components/BusinessCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import ShareButton from "@/components/ShareButton";
import { MapPin, Loader2, Building2, ArrowRight, Compass, Mail } from "lucide-react";

function setMeta(name, content, attr = "name") {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function CityPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const state = params.get("state") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [email, setEmail] = useState("");
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    setLoading(true);
    setErr(false);
    setJoined(false);
    api.get("/city-page", { params: { slug, ...(state ? { state } : {}) } })
      .then(({ data }) => setData(data))
      .catch(() => setErr(true))
      .finally(() => setLoading(false));
  }, [slug, state]);

  useEffect(() => {
    if (!data) return;
    const c = data.city;
    const label = `${c.name}, ${c.state_abbr}`;
    const n = data.businesses.length;
    const title = `Local businesses in ${label} | Saving America Money`;
    const desc = n
      ? `Discover ${n} trusted local ${n === 1 ? "business" : "businesses"} in ${label}. Reviews, deals and postcard offers from the Saving America Money hometown directory.`
      : `Explore local businesses near ${label} on Saving America Money — the hometown directory helping neighbors shop local and save.`;
    document.title = title;
    setMeta("description", desc);
    let canon = document.head.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement("link"); canon.setAttribute("rel", "canonical"); document.head.appendChild(canon); }
    canon.setAttribute("href", `${window.location.origin}/city/${c.slug}?state=${c.state_abbr}`);
    setMeta("og:title", title, "property");
    setMeta("og:description", desc, "property");
    const feat = data.businesses[0]?.name || "";
    const ogImg = `${process.env.REACT_APP_BACKEND_URL}/api/og-image?kind=city&title=${encodeURIComponent(c.name)}&subtitle=${encodeURIComponent(n ? `${n} local ${n === 1 ? "business" : "businesses"} · ${c.state_abbr}` : `${c.state_abbr} · shop local, save local`)}${feat ? `&featured=${encodeURIComponent(feat)}` : ""}`;
    setMeta("og:image", ogImg, "property");
    setMeta("og:image:width", "1200", "property");
    setMeta("og:image:height", "630", "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:image", ogImg);
    return () => {
      document.title = "Saving America Money";
      document.head.querySelector('link[rel="canonical"]')?.remove();
      ['meta[property="og:title"]', 'meta[property="og:description"]', 'meta[property="og:image"]',
       'meta[property="og:image:width"]', 'meta[property="og:image:height"]',
       'meta[name="twitter:card"]', 'meta[name="twitter:image"]'].forEach((s) => document.head.querySelector(s)?.remove());
    };
  }, [data]);

  const joinWaitlist = async () => {
    if (!email.trim() || !data) return;
    try {
      await api.post("/waitlist", { email: email.trim(), city_slug: data.city.slug, state_abbr: data.city.state_abbr, city_name: data.city.name });
      setJoined(true);
      setEmail("");
      toast.success(`You're on the list for ${data.city.name}!`);
    } catch {
      toast.error("Could not join the waitlist. Please try again.");
    }
  };

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sam-crimson" /></div>;
  if (err || !data) return (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="font-display text-2xl font-bold text-sam-navy">City not found</h1>
      <p className="text-sam-slate mt-2">We couldn't find that market yet.</p>
      <Button asChild className="mt-6 bg-sam-navy hover:bg-sam-navy2"><Link to="/browse">Browse all businesses</Link></Button>
    </div>
  );

  const { city, businesses, metro_cities, nearby } = data;
  const label = `${city.name}, ${city.state_abbr}`;
  const hasMetro = city.metro && city.metro_slug && city.metro !== city.name;

  return (
    <div data-testid="city-page" className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
      {/* Hero */}
      <div className="rounded-2xl bg-gradient-to-br from-sam-navy to-sam-navy2 text-sam-cream p-8 sm:p-12 mb-8">
        <div className="flex items-center gap-2 text-sam-cream/70 text-sm font-semibold uppercase tracking-wider">
          <MapPin className="h-4 w-4" />{city.state_abbr}
          {hasMetro && (
            <Link to={`/metro/${city.metro_slug}?state=${city.state_abbr}`} className="ml-1 text-sam-cream/60 hover:text-sam-cream underline-offset-2 hover:underline">· {city.metro} area</Link>
          )}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold mt-2" data-testid="city-page-title">Local businesses in {label}</h1>
        <p className="text-sam-cream/80 mt-3 max-w-2xl text-base">
          {businesses.length
            ? `${businesses.length} trusted local ${businesses.length === 1 ? "business" : "businesses"} to discover, review and support in ${city.name}.`
            : `We're building out ${city.name}. Explore nearby markets below or browse the full directory.`}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button asChild className="bg-sam-crimson hover:bg-sam-crimson/90 rounded-full" data-testid="city-page-browse-btn">
            <Link to={`/browse?state=${city.state_abbr}&city=${city.slug}`}>See all in Browse <ArrowRight className="ml-1 h-4 w-4" /></Link>
          </Button>
          <ShareButton title={`Local businesses in ${label}`} text={`Discover local businesses in ${label} on Saving America Money`}
            label="Share" testid="share-city-btn"
            className="rounded-full bg-white/10 border-white/30 text-sam-cream hover:bg-white/20 hover:text-white" />
        </div>
      </div>

      {/* Businesses */}
      {businesses.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="city-page-results">
          {businesses.map((b, i) => <BusinessCard key={b.id} b={b} index={i} />)}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-border p-10 text-center" data-testid="city-page-empty">
          <Building2 className="h-8 w-8 text-sam-slate mx-auto mb-3" />
          <p className="font-display text-lg text-sam-navy">No listings in {city.name} yet.</p>
          <p className="text-sam-slate text-sm mt-1">Be the first to know when {city.name} businesses join.</p>
          {joined ? (
            <p className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-sam-green" data-testid="waitlist-success"><Mail className="h-4 w-4" />You're on the list — we'll email you when {city.name} opens.</p>
          ) : (
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-2 max-w-md mx-auto">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && joinWaitlist()}
                placeholder="you@email.com" data-testid="waitlist-email" className="sm:flex-1" />
              <Button onClick={joinWaitlist} className="bg-sam-navy hover:bg-sam-navy2 w-full sm:w-auto" data-testid="waitlist-submit">Notify me</Button>
            </div>
          )}
        </div>
      )}

      {/* Nearby fallback */}
      {nearby && nearby.length > 0 && (
        <section className="mt-10" data-testid="city-page-nearby">
          <div className="flex items-center gap-2 font-display font-bold text-sam-navy mb-4"><Compass className="h-5 w-5 text-sam-crimson" />Nearby markets with listings</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {nearby.map((n) => (
              <Link key={`${n.state_abbr}-${n.slug}`} to={`/city/${n.slug}?state=${n.state_abbr}`}
                data-testid={`nearby-city-${n.slug}`}
                className="rounded-xl border border-border bg-card p-4 hover:border-sam-crimson hover:shadow-md transition-all">
                <div className="font-display font-bold text-sam-navy">{n.name}, {n.state_abbr}</div>
                <div className="text-xs text-sam-slate mt-1">{n.count} {n.count === 1 ? "business" : "businesses"} · {n.distance_mi} mi away</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Metro rollup */}
      {metro_cities && metro_cities.length > 0 && (
        <section className="mt-10" data-testid="city-page-metro">
          <div className="flex items-center gap-2 font-display font-bold text-sam-navy mb-4">
            <MapPin className="h-5 w-5 text-sam-crimson" />
            {hasMetro ? (
              <Link to={`/metro/${city.metro_slug}?state=${city.state_abbr}`} className="hover:text-sam-crimson transition-colors" data-testid="city-page-metro-link">More in the {city.metro} area</Link>
            ) : (
              <span>More in the {city.metro} area</span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {metro_cities.map((m) => (
              <Link key={`${m.state_abbr}-${m.slug}`} to={`/city/${m.slug}?state=${m.state_abbr}`}
                data-testid={`metro-city-${m.slug}`}
                className={`text-sm px-3 py-1.5 rounded-full border transition ${m.count ? "border-sam-crimson/40 text-sam-navy hover:bg-sam-crimson hover:text-white" : "border-border text-sam-slate hover:border-sam-crimson"}`}>
                {m.name}{m.count ? ` · ${m.count}` : ""}
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
